import fallbackPosts from '../data/posts.json';
import profileData from '../data/profile.json';
import { cvMarkdown as fallbackCv } from '../data/cvData';

// Live GitHub Raw endpoints (allows editing directly on GitHub with 0 rebuilds)
const GITHUB_RAW_BASE = 'https://raw.githubusercontent.com/thedawie/PersonalWebsite/main/public';
const POSTS_RAW_URL = `${GITHUB_RAW_BASE}/data/posts.json`;
const CV_RAW_URL = `${GITHUB_RAW_BASE}/cv.md`;

class ContentService {
  /**
   * Fetch all posts (first tries GitHub Raw, then local /data/posts.json, then bundled fallback)
   * @returns {Promise<Array>} List of posts
   */
  async getPosts() {
    const timestamp = Date.now();

    // 1. Try Live GitHub Raw (Instant updates when editing on GitHub)
    try {
      const res = await fetch(`${POSTS_RAW_URL}?t=${timestamp}`, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
      }
    } catch (e) {
      // Ignore network errors and try local static path
    }

    // 2. Try Local static file
    try {
      const localRes = await fetch(`/data/posts.json?t=${timestamp}`);
      if (localRes.ok) {
        const localData = await localRes.json();
        if (Array.isArray(localData) && localData.length > 0) {
          return localData;
        }
      }
    } catch (e) {
      // Ignore local fetch failure
    }

    // 3. Bundled fallback
    return fallbackPosts;
  }

  /**
   * Fetch a single post by ID
   * @param {string} id
   * @returns {Promise<Object|null>}
   */
  async getPostById(id) {
    const posts = await this.getPosts();
    return posts.find((p) => p.id === id) || null;
  }

  /**
   * Fetch the CV content in Markdown
   * @returns {Promise<string>} Markdown text
   */
  async getCv() {
    const timestamp = Date.now();

    // 1. Try Live GitHub Raw
    try {
      const res = await fetch(`${CV_RAW_URL}?t=${timestamp}`, { cache: 'no-store' });
      if (res.ok) {
        const text = await res.text();
        if (text && text.trim().length > 0) {
          return text;
        }
      }
    } catch (e) {
      // Ignore network errors and try local
    }

    // 2. Try Local static file
    try {
      const localRes = await fetch(`/cv.md?t=${timestamp}`);
      if (localRes.ok) {
        const localText = await localRes.text();
        if (localText && localText.trim().length > 0) {
          return localText;
        }
      }
    } catch (e) {
      // Ignore
    }

    // 3. Bundled fallback
    return fallbackCv;
  }

  /**
   * Fetch Profile information
   * @returns {Promise<Object>} Profile details
   */
  async getProfile() {
    return Promise.resolve(profileData);
  }
}

export const contentService = new ContentService();
export default contentService;
